import React, { useState } from "react";
import { X, Flag, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  "Inappropriate behavior",
  "Harassment",
  "Fake profile",
  "Spam / scam",
  "Safety concern",
  "Other",
];

const ReportModal = ({ targetUser, onClose, onSubmitted }) => {
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [block, setBlock] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/safety/reports", {
        reported_user_id: targetUser.id,
        reason,
        details,
      });
      if (block) {
        await api.post(`/safety/block/${targetUser.id}`);
      }
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl flex items-end sm:items-center justify-center p-4"
      data-testid="report-modal"
    >
      <div className="w-full max-w-md bg-[#111] border border-white/10 rounded-3xl p-6 sm:p-7 relative">
        <button
          onClick={onClose}
          data-testid="report-close-btn"
          className="absolute top-4 right-4 text-neutral-400 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Flag className="text-red-400" size={18} />
          <p className="text-xs uppercase tracking-[0.3em] text-red-400 font-semibold">
            Report
          </p>
        </div>
        <h2 className="font-heading text-3xl mb-1">Report {targetUser.name}</h2>
        <p className="text-sm text-neutral-400 mb-5">
          We review every report within 24 hours. Threats get escalated immediately.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-2">
              Reason
            </label>
            <div className="flex flex-wrap gap-2">
              {REASONS.map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => setReason(r)}
                  data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${
                    reason === r
                      ? "bg-[#C6FF00] text-[#0A0A0A] border-[#C6FF00]"
                      : "bg-transparent text-neutral-300 border-white/15 hover:border-[#C6FF00]"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-2">
              Details (optional)
            </label>
            <textarea
              data-testid="report-details"
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="What happened?"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#C6FF00] resize-none"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={block}
              onChange={(e) => setBlock(e.target.checked)}
              data-testid="report-block-checkbox"
              className="accent-[#C6FF00]"
            />
            <span className="text-xs text-neutral-300">
              Also block this user (recommended)
            </span>
          </label>

          <button
            type="submit"
            data-testid="report-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold text-xs uppercase tracking-[0.25em] px-6 py-4 rounded-md transition-all disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit Report"} <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
